import { useEffect, useRef, useState } from "react"
import { Loader2 } from "lucide-react"
import { observer } from "mobx-react-lite"
import { Link, useNavigate } from "react-router-dom"

import { Button } from "@/components/button"
import { useScaleApplication } from "@/domains/application/use-scale-application"
import { MetaData } from "@/domains/common/layout/metadata"
import { PageContainer } from "@/domains/common/layout/page-container"
import { PageTitle } from "@/domains/common/layout/page-title"
import { LcAddress } from "@/domains/core"
import { ScaleCard300 } from "@/domains/design/components/scale-card-300"
import { isPendingGauge } from "@/domains/pending-creation"

const POLL_INTERVAL_MS = 2000
const TIMEOUT_MS = 120_000

/**
 * Holding page shown after user successfully creates a Gauge from Add Liquidity.
 * Polls until the gauge appears in the API with a valid displayName, then redirects to Vote or back to add liquidity.
 */
const GaugeCreatingPage = observer(() => {
  const scaleApp = useScaleApplication()
  const navigate = useNavigate()
  const pending = scaleApp.PendingCreation.pendingGauge
  const [timedOut, setTimedOut] = useState(false)
  const pollIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null)

  useEffect(() => {
    if (!pending || !isPendingGauge(pending)) {
      navigate("/liquidity", { replace: true })
      return
    }

    const pairAddressLc = new LcAddress(pending.pairAddress)

    const checkGauge = async () => {
      await scaleApp.Gauge.Orchestrator.query.forceExecute()
      const gauge = scaleApp.Gauge.Repository.findByRevenueSource(pairAddressLc)
      const displayName = (gauge?.revenueSource?.displayName ?? "").trim()
      if (displayName.length > 0) {
        if (pollIntervalRef.current) {
          clearInterval(pollIntervalRef.current)
          pollIntervalRef.current = null
        }
        scaleApp.PendingCreation.clearPendingGauge()
        navigate(`/liquidity/${pending.pairAddress}/add`, { replace: true })
      }
    }

    const initialTimeout = setTimeout(checkGauge, 1500)
    pollIntervalRef.current = setInterval(checkGauge, POLL_INTERVAL_MS)

    const timeoutId = setTimeout(() => {
      setTimedOut(true)
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current)
        pollIntervalRef.current = null
      }
    }, TIMEOUT_MS)

    return () => {
      clearTimeout(initialTimeout)
      if (pollIntervalRef.current) clearInterval(pollIntervalRef.current)
      clearTimeout(timeoutId)
    }
  }, [pending?.pairAddress, scaleApp, navigate])

  if (!pending || !isPendingGauge(pending)) {
    return null
  }

  return (
    <>
      <MetaData title="Creating gauge" />
      <PageContainer>
        <PageTitle title="Your gauge is being created" subTitle="The gauge will appear in the app shortly." />
        <ScaleCard300 className="flex flex-col items-center justify-center gap-6 rounded-lg border border-p300-light p-8 text-center text-t100 sm:p-12">
          {timedOut ? (
            <>
              <p className="text-t200">
                This is taking longer than usual. You can go to Vote or back to Add Liquidity and try again later.
              </p>
              <div className="flex gap-3">
                <Button variant="primary" asChild>
                  <Link to="/vote">Go to Vote</Link>
                </Button>
                <Button variant="neutral" asChild>
                  <Link to={`/liquidity/${pending.pairAddress}/add`}>Back to Add Liquidity</Link>
                </Button>
              </div>
            </>
          ) : (
            <>
              <Loader2 className="h-12 w-12 animate-spin text-primary-500" aria-hidden />
              <p className="text-t200">
                Creating gauge for <span className="font-medium text-t100">{pending.pairDisplayName}</span>…
              </p>
              <p className="text-sm text-t300">
                We are waiting for the gauge to appear in the system. This usually takes under a minute.
              </p>
            </>
          )}
        </ScaleCard300>
      </PageContainer>
    </>
  )
})

export default GaugeCreatingPage
